import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { useSyncExternalStore } from "react";
import { toast } from "sonner";

import { db } from "integrations/firebase/firestore";

/**
 * Deleting a notification writes two documents: the delete, and a tombstone at
 * users/{uid}/notification_tombstones/{id}. The backend producers use
 * deterministic ids and re-run (the hourly event-start sweep, a resumed
 * announcement fan-out), and without the tombstone they would write the
 * notification back and push it again. Both go in one batch, so the tombstone
 * exists exactly when the notification stops existing.
 *
 * Two writes per notification against Firestore's 500-write batch cap gives
 * 250 notifications per batch.
 */
export const DELETE_CHUNK_SIZE = 250;

/** How long the undo toast gives before the delete is actually written. */
export const UNDO_WINDOW_MS = 5000;

export interface DeletionResult {
  deleted: number;
  failed: number;
}

/**
 * Delete these notifications in chunks. A chunk that fails does not stop the
 * others: the caller gets the counts and says what happened.
 *
 * Every chunk is committed before any is awaited. A commit resolves only when
 * the server acknowledges it, so awaiting one at a time offline would leave
 * every later chunk unwritten, even locally. Committed together, all of them
 * land in the local cache at once and are sent when the connection returns.
 */
export async function deleteNotifications(
  uid: string,
  ids: readonly string[],
): Promise<DeletionResult> {
  const commits: { size: number; done: Promise<void> }[] = [];
  for (let start = 0; start < ids.length; start += DELETE_CHUNK_SIZE) {
    const chunk = ids.slice(start, start + DELETE_CHUNK_SIZE);
    const batch = writeBatch(db);
    for (const id of chunk) {
      batch.delete(doc(db, "users", uid, "notifications", id));
      batch.set(doc(db, "users", uid, "notification_tombstones", id), {
        deletedAt: serverTimestamp(),
      });
    }
    commits.push({ size: chunk.length, done: batch.commit() });
  }
  const outcomes = await Promise.allSettled(commits.map((c) => c.done));
  let deleted = 0;
  let failed = 0;
  outcomes.forEach((outcome, index) => {
    if (outcome.status === "fulfilled") deleted += commits[index].size;
    else failed += commits[index].size;
  });
  return { deleted, failed };
}

/**
 * Every notification the user has, not only the FEED_LIMIT the bell shows.
 * "Clear all" that left the 51st behind would surface it straight away.
 */
export async function clearAllNotifications(
  uid: string,
): Promise<DeletionResult> {
  const snapshot = await getDocs(collection(db, "users", uid, "notifications"));
  return deleteNotifications(
    uid,
    snapshot.docs.map((document) => document.id),
  );
}

/** How long Clear all waits for the server before letting the dialog go. */
export const CLEAR_ALL_BUDGET_MS = 10_000;

export type ClearAllOutcome =
  ({ status: "done" } & DeletionResult) | { status: "pending" };

/**
 * Clear all, bounded like forgetPushToken. Offline, the commits never
 * resolve, and a dialog waiting on them would sit on "Deleting…" with Cancel
 * disabled for as long as the connection is gone. After the budget this
 * answers "pending"; the deletes are already in the local cache and are sent
 * when the connection returns, and a failure then still gets a toast.
 */
export async function clearAllNotificationsWithin(
  uid: string,
  budgetMs = CLEAR_ALL_BUDGET_MS,
): Promise<ClearAllOutcome> {
  const work = clearAllNotifications(uid);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), budgetMs);
  });
  try {
    const first = await Promise.race([work, timedOut]);
    if (first !== "timeout") return { status: "done", ...first };
  } finally {
    clearTimeout(timer);
  }
  void work
    .then(({ failed }) => {
      if (failed > 0)
        toast.error(`${failed} notifications could not be deleted.`);
    })
    .catch(() => toast.error("Your notifications could not be cleared."));
  return { status: "pending" };
}

// ──── Undoable single delete ─────────────────────────────────────────────────
//
// The undo cannot recreate a deleted notification: the rules refuse a client
// create, and the tombstone would outlive it anyway. So the delete is held
// back for UNDO_WINDOW_MS and the item is only hidden meanwhile. The state
// lives here rather than in a component because the detail page navigates away
// the moment Delete is pressed, and the bell has to keep hiding the item.

interface Pending {
  uid: string;
  timer: ReturnType<typeof setTimeout>;
}

const pending = new Map<string, Pending>();
let hidden: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();

function publish() {
  hidden = new Set(pending.keys());
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Ids waiting out their undo window, which lists should leave out. */
export function useHiddenNotificationIds(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, () => hidden);
}

async function commit(id: string) {
  const entry = pending.get(id);
  if (!entry) return;
  clearTimeout(entry.timer);
  // The write is applied to the local cache before this resolves, so the
  // snapshot listeners drop the item before it is un-hidden here.
  const result = deleteNotifications(entry.uid, [id]);
  pending.delete(id);
  // The toast's own timer pauses on hover and this one does not, so an Undo
  // still on screen now would promise something it can no longer do.
  toast.dismiss(toastId(id));
  const { failed } = await result;
  publish();
  if (failed > 0) toast.error("Could not delete the notification.");
}

function toastId(id: string) {
  return `notification-deleted-${id}`;
}

function undo(id: string) {
  const entry = pending.get(id);
  if (!entry) return;
  clearTimeout(entry.timer);
  pending.delete(id);
  publish();
}

/**
 * Hide the notification now, offer an undo, and delete it when the window
 * closes. Closing the toast early deletes it early.
 */
export function scheduleNotificationDeletion(uid: string, id: string): void {
  if (pending.has(id)) return;
  pending.set(id, {
    uid,
    timer: setTimeout(() => void commit(id), UNDO_WINDOW_MS),
  });
  publish();
  toast("Notification deleted", {
    id: toastId(id),
    duration: UNDO_WINDOW_MS,
    action: { label: "Undo", onClick: () => undo(id) },
    // Also fires after Undo, which has already removed the entry by then.
    onDismiss: () => void commit(id),
  });
}

/**
 * Write every held-back delete now. Sign-out calls this first: once the user
 * is signed out the rules refuse the write, and the item would come back.
 */
export function flushNotificationDeletions(): void {
  for (const id of [...pending.keys()]) void commit(id);
}

/** Tests only: forget held-back deletes without writing them. */
export function resetPendingNotificationDeletions(): void {
  for (const entry of pending.values()) clearTimeout(entry.timer);
  pending.clear();
  publish();
}

if (typeof window !== "undefined") {
  // Best effort for a tab closed inside the undo window. The write is queued
  // before the page goes; whether it reaches the server first is up to the
  // browser, and when it does not the notification simply stays.
  window.addEventListener("pagehide", flushNotificationDeletions);
}
